/**
 * Scheduled payout runs: the next payout dates and when each is prepared,
 * and every run with its payouts, whether staff were told, and the bank file.
 *
 * The bank file carries full account numbers, so it is downloaded here —
 * never emailed — by somebody allowed to pay, once the run's payouts are
 * approved; every download is audited and counted on the run.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Play, Send } from 'lucide-react';
import {
  Alert,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  LoadingState,
  formatDate,
  formatDateTime,
  formatMoney,
  humanize,
  toast,
} from '@connect/web-shared';

import { partnerKeys, partnersApi } from './api';
import { Failed, errorText } from './shared';

export function PayoutRuns() {
  const queryClient = useQueryClient();
  const runs = useQuery({ queryKey: partnerKeys.runs, queryFn: partnersApi.runs });
  const refresh = () => queryClient.invalidateQueries({ queryKey: partnerKeys.all });
  const prepare = useMutation({
    mutationFn: partnersApi.prepareRuns,
    onSuccess: async (done) => {
      const passed = done.passed_over.map((p) => `${p.partner}: ${humanize(p.reason)}`).join('; ');
      toast.success(
        `${done.prepared.length} payout(s) prepared`,
        passed ? `Passed over — ${passed}` : done.told ? 'Staff have been emailed.' : undefined,
      );
      await refresh();
    },
    onError: (e) => toast.error('Could not prepare', errorText(e)),
  });
  const resend = useMutation({
    mutationFn: (id: string) => partnersApi.resendNotice(id),
    onSuccess: async (result) => {
      if (result.notice_error) toast.error('Not sent', result.notice_error);
      else toast.success('Sent again');
      await refresh();
    },
  });
  const download = useMutation({
    mutationFn: (id: string) => partnersApi.bankFile(id),
    onSuccess: () => void refresh(),
    onError: (e) => toast.error('No bank file', errorText(e)),
  });

  if (runs.isPending) return <LoadingState />;
  if (runs.isError) return <Failed error={runs.error} onRetry={() => void runs.refetch()} />;
  const data = runs.data;
  return (
    <div className="space-y-4">
      {data.notice_to.length === 0 ? (
        <Alert tone="warning" title="Nobody is emailed about runs">
          Set who is told under Commissions, Programme rules.
        </Alert>
      ) : null}
      <Card>
        <CardHeader
          title="Coming up"
          description={`${data.programme_plan}. Prepared ${data.notice_bank_days} bank working day(s) before each date. Partners with a plan of their own keep their own dates.`}
          actions={
            <Can permission="partners.payout.manage">
              <Button loading={prepare.isPending} onClick={() => prepare.mutate()}>
                <Play className="size-4" />
                Prepare due runs now
              </Button>
            </Can>
          }
        />
        <CardBody>
          {data.upcoming.length === 0 ? (
            <p className="text-sm text-muted">The programme pays on request only.</p>
          ) : (
            <ul className="divide-y divide-line text-sm">
              {data.upcoming.map((u) => (
                <li key={u.payout_on} className="flex justify-between py-2">
                  <span className="font-medium">{formatDate(u.payout_on)}</span>
                  <span className="text-muted">prepared on {formatDate(u.prepared_on)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="Runs" description="Newest first." />
        <CardBody className="divide-y divide-line">
          {data.runs.length === 0 ? <p className="text-sm text-muted">No run prepared yet.</p> : null}
          {data.runs.map((run) => (
            <div key={run.public_id} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
              <div>
                <p className="font-medium">
                  {formatDate(run.payout_on)} · <span className="font-mono text-xs">{run.reference}</span>
                </p>
                <p className="text-muted">
                  {run.payouts} payout(s) · {formatMoney(run.gross)} gross
                </p>
                <p className="text-xs text-faint">
                  {run.notified_at ? `Staff emailed ${formatDateTime(run.notified_at)}` : (run.notice_error ?? 'Not emailed yet')}
                  {run.bank_file_downloads ? ` · bank file downloaded ${run.bank_file_downloads}×` : ''}
                </p>
              </div>
              <Can permission="partners.payout.manage">
                <div className="flex gap-2">
                  <Button size="sm" variant="secondary" loading={resend.isPending} onClick={() => resend.mutate(run.public_id)}>
                    <Send className="size-3.5" />
                    Email again
                  </Button>
                  <Button size="sm" loading={download.isPending} onClick={() => download.mutate(run.public_id)}>
                    <Download className="size-3.5" />
                    Bank file
                  </Button>
                </div>
              </Can>
            </div>
          ))}
        </CardBody>
      </Card>
    </div>
  );
}
